<template>
  <nav
    class="channel-top-bar flex w-full flex-col gap-2 px-4 py-2 large-desktop:flex-row large-desktop:items-center large-desktop:justify-between large-desktop:gap-4 large-desktop:px-5"
    :aria-label="$t('channel.nav.channelNavigation')"
  >
    <!-- Section tabs: ACTIVITIES / REWARDS / ANNOUNCEMENTS. A single grouped pill
         (translucent brand fill) holds the three text pills. Anchor/scroll nav to
         page sections; active state derives from
         the current route hash. -->
    <ul
      class="flex h-[52px] min-w-0 shrink-0 items-center self-start overflow-x-auto rounded-full bg-fill-brand p-1 backdrop-blur-[5px] hide-scrollbar large-desktop:self-auto"
    >
      <li v-for="tab in sectionTabs" :key="tab.hash" class="shrink-0">
        <!--
          Tab styling: a SOLID brand-red group container (`bg-fill-brand`) holding flush
          tab pills with WHITE uppercase labels (`text-text-on-brand`). The design
          shows no active-pill color change — the active tab is conveyed by a
          subtle lighter inset pill (`bg-fill-brand-secondary-hover`) plus
          `aria-current`, which keeps white text passing WCAG-AA on the red
          group (the legacy pale `fill-brand-secondary` container forced black
          inactive text, which did not match the design).

          Scroll-anchor tabs (ACTIVITIES / REWARDS) render as RouterLinks; the
          ANNOUNCEMENTS tab is a modal trigger rendered as a <button>
          that opens the announcements modal. Both share identical styling +
          testids + active-state so a11y and the tab contract are preserved.
        -->
        <RouterLink
          v-if="tab.to"
          :to="tab.to"
          :data-testid="`channel-topbar-tab-${tab.key}`"
          class="inline-flex h-full items-center whitespace-nowrap rounded-full pl-4 pr-5 font-serif text-sm uppercase leading-[22px] text-text-on-brand transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-brand"
          :class="
            activeHash === tab.hash
              ? 'bg-fill-brand-secondary-hover'
              : 'hover:bg-fill-brand-secondary'
          "
          :aria-current="activeHash === tab.hash ? 'true' : undefined"
        >
          {{ $t(tab.labelKey) }}
        </RouterLink>
        <button
          v-else
          type="button"
          :data-testid="`channel-topbar-tab-${tab.key}`"
          class="inline-flex h-full items-center whitespace-nowrap rounded-full pl-4 pr-5 font-serif text-sm uppercase leading-[22px] text-text-on-brand transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-brand"
          :class="
            activeHash === tab.hash
              ? 'bg-fill-brand-secondary-hover'
              : 'hover:bg-fill-brand-secondary'
          "
          :aria-current="activeHash === tab.hash ? 'true' : undefined"
          @click="tab.action?.()"
        >
          {{ $t(tab.labelKey) }}
        </button>
      </li>
    </ul>

    <!-- Right cluster. The top bar holds ONLY the tab pill group
         (left) and the red MESSAGES pill (right). The wallet connect, Intercom,
         auto-translation control and the standalone member-count chip are NOT in
         the new-layout top bar and have been removed; the member avatars live INSIDE
         the MESSAGES pill. -->
    <div
      class="flex w-full shrink-0 items-center gap-2 large-desktop:w-auto large-desktop:justify-end"
    >
      <!-- MESSAGES pill. A single brand-red rounded strip that holds two sibling
           controls: the chat toggle (icon + label + unread badge) and the member
           avatars (opens the member list). On mobile it stretches full width with
           the avatars pinned to the right edge, matching the design's mobile top
           treatment. -->
      <div
        class="flex h-[52px] min-w-0 flex-1 items-center justify-between gap-2 rounded-full border-[3px] border-fill-brand bg-fill-brand py-1 pl-2 pr-4 large-desktop:flex-none large-desktop:gap-3"
      >
        <!-- chatOpen on large-desktop flips the two-column shell; on mobile/tablet
             it opens the chat modal. The shell owns that breakpoint branch via the
             emitted toggle-chat intent. -->
        <button
          type="button"
          data-testid="channel-topbar-messages-toggle"
          class="flex min-w-0 items-center gap-2 rounded-full text-text-on-brand focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-brand"
          :aria-label="$t('channel.nav.messages')"
          :aria-pressed="largeDesktop ? chatOpen : undefined"
          @click="onToggleChat"
        >
          <Icon
            icon="mdi:message-text"
            class="size-7 shrink-0 text-text-on-brand"
            aria-hidden="true"
          />
          <span class="truncate font-serif text-sm uppercase leading-[22px]">{{
            $t("channel.nav.messages")
          }}</span>
        </button>

        <!-- Member avatars. Channel-home only. Issues its OWN URQL query; the
             hero's identical Apollo query is gated OFF under the new layout, so
             this is the sole owner here. Clicking opens the member list. -->
        <button
          v-if="isChannelHome"
          type="button"
          data-testid="channel-topbar-members"
          :aria-label="$t('channel.nav.memberList')"
          :disabled="channelStore.isLeaderboardDisabled"
          class="flex shrink-0 items-center rounded-full transition-opacity hover:opacity-90 disabled:cursor-default disabled:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-brand"
          @click="onMemberListClick"
        >
          <span class="relative inline-flex items-center -space-x-2.5">
            <Avatar
              v-for="(entry, index) in leaderboardEntries"
              :key="entry.user.id"
              :user="{
                username: entry.user.username,
                avatar: entry.user.avatar,
                id: entry.user.id,
              }"
              size="md"
              class="rounded-full border-2 border-fill-brand"
              :style="{ 'z-index': leaderboardEntries.length - index }"
            />
          </span>
        </button>
      </div>
    </div>
  </nav>

  <!-- The top bar lives in the shell (outside channel.vue), so it owns its own
       member-list modal instance. -->
  <LeaderboardModal v-if="isChannelHome" v-model:is-open="showMemberList" />
</template>

<script lang="ts">
import { graphql as gql } from "@/generated";

/**
 * Section anchors the tabs scroll to. ACTIVITIES → the QUESTS/contests region
 * (`#contests`), REWARDS → the offers section (`#offers`), ANNOUNCEMENTS →
 * the threads/announcements surface (`#threads`). Existing `#contests` /
 * `#threads` ids live in channel.vue today; `#offers` is added alongside it.
 */
const SECTION_HASHES = {
  activities: "#contests",
  rewards: "#offers",
  announcements: "#threads",
} as const;

/**
 * The member-avatar query. This is the IDENTICAL document string used by
 * channel-hero.vue. channel-hero uses @vue/apollo-composable (legacy) whose
 * result objects cannot be shared with a URQL consumer — the clients are
 * entirely separate. The top bar therefore issues its OWN URQL useQuery below.
 *
 * Apollo and URQL are SEPARATE network clients and do NOT deduplicate each
 * other's requests. To avoid a redundant fetch under the new layout, the hero's
 * Apollo query is disabled when enable_new_channel_layout is ON (see
 * channel-hero.vue), so this URQL query is the sole member-avatar fetch then.
 */
const ChannelHeroMembersDocument = gql(/* GraphQL */ `
  query channelHeroMembers($channel_id: String!, $community_id: String) {
    channelLeaderboard(
      channel_id: $channel_id
      highlights: false
      community_id: $community_id
      page: 0
      limit: 3
    ) {
      ... on Leaderboard {
        ranking {
          user {
            username
            avatar
            id
          }
        }
      }
    }
  }
`);
</script>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useRoute, type RouteLocationRaw } from "vue-router";
import { useQuery } from "@urql/vue";
import { Icon } from "@iconify/vue";
import { RouteNames } from "@/router/app.router";
import { useChannelStore } from "@/stores/channel.store";
import { useTrackingStore } from "@/stores/tracking.store";
import {
  EventCategory,
  TrackEventEnum,
} from "@/services/tracking/tracking-types";
import Avatar from "../me/avatar.vue";
import LeaderboardModal from "@/components/leaderboard/leaderboard-modal.vue";
import { useAnnouncementsModal } from "@/composables/channel/use-announcements-modal";

/**
 * chatOpen + largeDesktop are owned by the channel-layout-v2.vue shell
 * and passed down via its #topbar scoped slot. The top
 * bar emits a single `toggle-chat` intent and the shell decides column-vs-modal
 * by breakpoint, keeping the breakpoint logic in ONE place (the shell).
 */
const props = defineProps<{
  chatOpen: boolean;
  largeDesktop: boolean;
  /**
   * Whether the Offers/Rewards surface is enabled (enable_coupon_offers). When
   * false the "Rewards" tab is dropped: its `#offers` scroll anchor (the
   * <OffersSection> in channel.vue) is not rendered when offers are hidden, so
   * the tab would otherwise scroll nowhere.
   */
  enableCouponOffers: boolean;
}>();

const emit = defineEmits<{
  "toggle-chat": [];
}>();

const route = useRoute();
const channelStore = useChannelStore();
const trackingStore = useTrackingStore();
const { open: openAnnouncements } = useAnnouncementsModal();

const isChannelHome = computed(() => route.name === RouteNames.Channel);

/**
 * Active-tab styling derives from the current route hash. A simple computed off
 * route.hash is sufficient — no scroll-spy.
 */
const activeHash = computed(() => route.hash || SECTION_HASHES.activities);

function sectionLink(hash: string): RouteLocationRaw {
  return {
    name: RouteNames.Channel,
    params: { channel: channelStore.channelSlug },
    hash,
  };
}

/**
 * Section tabs. ACTIVITIES / REWARDS are scroll-anchor RouterLinks (`to` set).
 * ANNOUNCEMENTS is a MODAL trigger: it opens the
 * announcements modal rather than scrolling to an inline section, so it is an
 * action tab (`action` set, no `to`) rendered as a <button>. The active-state
 * styling still derives from `activeHash` against each tab's `hash`.
 */
const sectionTabs = computed(() => [
  {
    key: "activities",
    hash: SECTION_HASHES.activities,
    labelKey: "channel.navigation.activities",
    to: sectionLink(SECTION_HASHES.activities),
    action: undefined,
  },
  // Rewards (#offers) only when the offers surface is enabled — otherwise the
  // scroll target does not exist and the tab is a dead link.
  ...(props.enableCouponOffers
    ? [
        {
          key: "rewards",
          hash: SECTION_HASHES.rewards,
          labelKey: "channel.navigation.rewards",
          to: sectionLink(SECTION_HASHES.rewards),
          action: undefined,
        },
      ]
    : []),
  {
    key: "announcements",
    hash: SECTION_HASHES.announcements,
    labelKey: "channel.navigation.announcements",
    to: undefined,
    action: openAnnouncements,
  },
]);

/**
 * Member avatars. Scoped to channel-home via `pause` so the query does NOT
 * execute on other routes; the avatar section is also `v-if`-guarded in the
 * template so it does not mount outside channel-home.
 */
const { data: membersData } = useQuery({
  query: ChannelHeroMembersDocument,
  variables: computed(() => ({
    channel_id: channelStore.channelSlug!,
    community_id: channelStore.selectedCommunity ?? null,
  })),
  pause: computed(() => !isChannelHome.value || !channelStore.channelSlug),
});

const leaderboardEntries = computed(() => {
  const board = membersData.value?.channelLeaderboard;
  return board?.__typename === "Leaderboard" ? board.ranking : [];
});

const showMemberList = ref(false);

function onMemberListClick() {
  trackingStore.track({
    event: TrackEventEnum.MemberListClicked,
    category: EventCategory.Channels,
  });

  if (channelStore.isLeaderboardDisabled) return;
  showMemberList.value = true;
}

function onToggleChat() {
  emit("toggle-chat");
}
</script>
